import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface Props {
  /** Raw string value from the form ("" = inherit the quote markup). */
  value: string;
  onChange: (value: string) => void;
  /** Net cost of this line (already multiplied out) used for the live preview. */
  netTotal?: number;
  /** Markup that applies when no override is set. */
  defaultMarkupPct?: number | null;
  currencySymbol?: string;
}

export function MarkupOverrideField({
  value,
  onChange,
  netTotal = 0,
  defaultMarkupPct,
  currencySymbol = "",
}: Props) {
  const hasOverride = value !== "" && Number.isFinite(parseFloat(value));
  const inherited = Number(defaultMarkupPct) || 0;
  const applied = hasOverride ? parseFloat(value) : inherited;
  const clientPrice = netTotal * (1 + applied / 100);

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <Label htmlFor="markup_percentage" className="text-sm font-medium">
        Markup override (%)
      </Label>
      <Input
        id="markup_percentage"
        type="number"
        step="0.01"
        min={0}
        max={999}
        placeholder={`Default${defaultMarkupPct ? ` (${inherited}%)` : " (inherits quote markup)"}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        {hasOverride
          ? `This item is marked up at ${applied}% instead of the quote markup.`
          : "Leave blank to use the markup set on the quote."}
      </p>
      {netTotal > 0 && (
        <p className="text-xs text-muted-foreground">
          Net cost: {currencySymbol}
          {netTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} · Markup: {applied}% ·
          Client price: {currencySymbol}
          {clientPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </p>
      )}
    </div>
  );
}
